import React, {useEffect, useRef, useState} from 'react';
import './EMDRVisual.css';

const overlayTranslations = {
  es: 'Presiona barra espaciadora para iniciar',
  en: 'Press spacebar to start',
  ar: 'اضغط شريط المسافة للبدء',
  fr: 'Appuyez sur la barre d’espace pour démarrer',
  pt: 'Pressione a barra de espaço para iniciar',
  de: 'Drücken Sie die Leertaste, um zu starten',
  pl: 'Naciśnij spację, aby rozpocząć',
  it: 'Premi la barra spaziatrice per iniziare',
  ko: '스페이스바를 눌러 시작',
  zh: '按空格键开始',
  ja: 'スペースキーで開始',
  he: 'לחץ על מקש הרווח כדי להתחיל',
  ru: 'Нажмите пробел, чтобы начать'
};

const EMDRVisual = ({
  isActive = false,
  speed = 50,
  pattern = 'horizontal',
  size = 80,
  repetitions = null,
  soundEnabled = false,
  soundTone = 800,
  soundVolume = 30,
  onRepetitionsComplete = null,
  language = 'es',
  hideOverlay = false,
  containerRef = null,
}) => {
  const circleRef = useRef(null);
  const [showOverlay, setShowOverlay] = useState(() => {
    const seen = sessionStorage.getItem('emdr_overlay_seen');
    return seen !== 'true';
  });
  const initialWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
  const initialHeight = typeof window !== 'undefined' ? window.innerHeight : 0;
  const [dimensions, setDimensions] = useState({width: initialWidth, height: initialHeight});
  const [position, setPosition] = useState({x: initialWidth / 2 - 40, y: initialHeight / 2 - 40});
  const animationRef = useRef(null);
  const startTimeRef = useRef(null);
  const isActiveRef = useRef(isActive);
  const soundEnabledRef = useRef(soundEnabled);
  const soundToneRef = useRef(soundTone);
  const soundVolumeRef = useRef(soundVolume);
  const speedRef = useRef(speed);
  const sizeRef = useRef(size);
  const dimensionsRef = useRef(dimensions);
  const audioContextRef = useRef(null);
  const repetitionsCountRef = useRef(0);
  const cycleStateRef = useRef({
    hasBeenRight: false,
    hasBeenLeft: false,
    lastWasCenter: true
  });
  const soundStateRef = useRef({
    lastRightPlayed: false,
    lastLeftPlayed: false
  });

  // Actualizar referencias de sonido
  useEffect(() => {
    soundEnabledRef.current = soundEnabled;
    soundToneRef.current = soundTone;
    soundVolumeRef.current = soundVolume;
  }, [soundEnabled, soundTone, soundVolume]);

  // Actualizar referencias de parámetros para evitar reiniciar animación
  useEffect(() => {
    speedRef.current = speed;
  }, [speed]);

  useEffect(() => {
    sizeRef.current = size;
  }, [size]);

  useEffect(() => {
    dimensionsRef.current = dimensions;
  }, [dimensions]);

  // Función para reproducir un bip con panning estéreo
  // direction: 'left' para izquierda, 'right' para derecha
  const playBeep = (direction) => {
    if (!soundEnabledRef.current) return;
    
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }

      const audioContext = audioContextRef.current;
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }

      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      const pannerNode = audioContext.createStereoPanner();
      
      // Conectar: oscillator -> gain -> panner -> destination
      oscillator.connect(gainNode);
      gainNode.connect(pannerNode);
      pannerNode.connect(audioContext.destination);
      
      // Panning: -1 = izquierda completa, 1 = derecha completa
      pannerNode.pan.value = direction === 'left' ? -1 : 1;
      
      // Usar el tono configurado (convertir de porcentaje a valor de ganancia 0-1)
      oscillator.frequency.value = soundToneRef.current;
      oscillator.type = 'sine';
      
      // Usar el volumen configurado (0-100% -> 0-1)
      const volumeGain = soundVolumeRef.current / 100;
      gainNode.gain.setValueAtTime(volumeGain, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.1);
    } catch (error) {
      console.error('Error playing beep:', error);
    }
  };

  const isContained = Boolean(containerRef?.current);

  // Actualizar dimensiones cuando cambia el tamaño de la ventana o contenedor
  useEffect(() => {
    if (containerRef?.current) {
      const element = containerRef.current;
      const updateFromElement = () => {
        const rect = element.getBoundingClientRect();
        setDimensions({width: rect.width, height: rect.height});
      };
      updateFromElement();
      const observer = new ResizeObserver(updateFromElement);
      observer.observe(element);
      return () => observer.disconnect();
    }

    const updateDimensions = () => {
      setDimensions({width: window.innerWidth, height: window.innerHeight});
    };
    window.addEventListener('resize', updateDimensions);
    updateDimensions();
    return () => window.removeEventListener('resize', updateDimensions);
  }, [containerRef]);

  // Actualizar referencia de isActive
  useEffect(() => {
    isActiveRef.current = isActive;
  }, [isActive]);

  const startAnimation = () => {
    // Cancelar cualquier animación previa
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }

    if (!dimensions.width || !dimensions.height) {
      return;
    }

    if (!isActiveRef.current) {
      return;
    }

    const duration = 2000 - (speedRef.current * 18); // Entre 200ms (rápido) y 2000ms (lento)
    startTimeRef.current = performance.now();

    const animate = (currentTime) => {
      const currentDimensions = dimensionsRef.current;
      // Verificar si la animación debe continuar
      if (!isActiveRef.current || !currentDimensions.width || !currentDimensions.height) {
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
          animationRef.current = null;
        }
        return;
      }
      
      // Verificar si el sonido está habilitado antes de detener la animación
      // Esto asegura que el sonido continúe hasta que se completen todas las repeticiones

      const elapsed = currentTime - startTimeRef.current;
      // Un ciclo completo: derecha -> izquierda (una ida y vuelta)
      const currentDuration = 2000 - (speedRef.current * 18);
      const fullCycle = currentDuration * 2; // Duración para ir de derecha a izquierda y volver
      const cycleProgress = (elapsed % fullCycle) / fullCycle; // 0 a 1
      const currentSize = sizeRef.current;
      const halfSize = currentSize / 2;
      const centerX = currentDimensions.width / 2;
      const centerY = currentDimensions.height / 2;

      // La esfera debe llegar hasta los bordes exactos de la pantalla
      // position.x es la posición del borde izquierdo de la esfera
      // rightEdge: cuando position.x = width - size, el borde derecho de la esfera toca el borde derecho de la pantalla
      // leftEdge: cuando position.x = 0, el borde izquierdo de la esfera toca el borde izquierdo de la pantalla
      const rightEdge = currentDimensions.width - currentSize;
      const leftEdge = 0;
      const topEdge = 0;
      const bottomEdge = currentDimensions.height - currentSize;
      const centerPos = centerX - halfSize;
      
      // Movimiento completamente uniforme usando función seno
      // El seno oscila suavemente de -1 a 1
      const angle = cycleProgress * Math.PI * 2; // 0 a 2π
      let sineValue = Math.sin(angle); // Oscila de -1 a 1
      
      // Mapear seno [-1,1] a posición [derecha, izquierda]
      // Invertir para que comience desde la derecha: seno=1 → derecha, seno=-1 → izquierda
      const positionProgress = (-sineValue + 1) / 2; // [0,1] donde 0=izquierda, 1=derecha
      
      // Interpolación lineal uniforme
      const x = leftEdge + positionProgress * (rightEdge - leftEdge);
      let y = centerY - halfSize;

      if (pattern === 'diagonal-right') {
        // De abajo-izquierda a arriba-derecha y viceversa
        y = topEdge + (1 - positionProgress) * (bottomEdge - topEdge);
      } else if (pattern === 'diagonal-left') {
        // De arriba-izquierda a abajo-derecha y viceversa
        y = topEdge + positionProgress * (bottomEdge - topEdge);
      }
      
      setPosition({x: x, y: y});

      // Reproducir sonido cuando llega a los extremos - usar la posición real en lugar de sineValue
      if (soundEnabledRef.current) {
        const soundState = soundStateRef.current;
        const threshold = 2; // Margen de píxeles para considerar que está en el extremo
        
        // Detectar cuando llega a la derecha (positionProgress cerca de 1)
        if (positionProgress >= 0.99 && !soundState.lastRightPlayed) {
          playBeep('right');
          soundState.lastRightPlayed = true;
        } else if (positionProgress < 0.95) {
          soundState.lastRightPlayed = false;
        }
        
        // Detectar cuando llega a la izquierda (positionProgress cerca de 0)
        if (positionProgress <= 0.01 && !soundState.lastLeftPlayed) {
          playBeep('left');
          soundState.lastLeftPlayed = true;
        } else if (positionProgress > 0.05) {
          soundState.lastLeftPlayed = false;
        }
      }

      // Contar ciclos: un ciclo completo = pasar por el centro después de haber estado en ambos extremos
      if (repetitions !== null && repetitions > 0) {
        const state = cycleStateRef.current;
        
        // Detectar cuando está en la derecha (seno cercano a 1)
        if (sineValue > 0.9) {
          state.hasBeenRight = true;
        }
        // Detectar cuando está en la izquierda (seno cercano a -1)
        if (sineValue < -0.9) {
          state.hasBeenLeft = true;
        }
        // Detectar cuando pasa por el centro (seno cercano a 0)
        if (Math.abs(sineValue) < 0.1) {
          // Si ha pasado por ambos extremos y ahora está en el centro, es un ciclo completo
          if (state.hasBeenRight && state.hasBeenLeft && !state.lastWasCenter) {
            repetitionsCountRef.current += 1;
            state.hasBeenRight = false;
            state.hasBeenLeft = false;
            
            if (repetitionsCountRef.current >= repetitions) {
              // Detener la animación solo después de completar todas las repeticiones
              // Asegurar que el sonido también se detenga
              if (onRepetitionsComplete) {
                onRepetitionsComplete();
              }
              // Detener la animación
              if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
                animationRef.current = null;
              }
              // Resetear el estado del sonido
              soundStateRef.current = {
                lastRightPlayed: false,
                lastLeftPlayed: false
              };
              stopAnimation();
              return;
            }
          }
          state.lastWasCenter = true;
        } else {
          state.lastWasCenter = false;
        }
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);
  };

  const stopAnimation = () => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    
    if (dimensions.width && dimensions.height) {
      const centerX = dimensions.width / 2;
      const centerY = dimensions.height / 2;
      const halfSize = size / 2;
      setPosition({x: centerX - halfSize, y: centerY - halfSize});
    }
  };

  // Controlar animación cuando cambia isActive
  useEffect(() => {
    if (hideOverlay && showOverlay) {
      setShowOverlay(false);
    }
    if (isActive) {
      if (showOverlay) {
        sessionStorage.setItem('emdr_overlay_seen', 'true');
        setShowOverlay(false);
      }
      repetitionsCountRef.current = 0;
      cycleStateRef.current = {
        hasBeenRight: false,
        hasBeenLeft: false,
        lastWasCenter: true
      };
      soundStateRef.current = {
        lastRightPlayed: false,
        lastLeftPlayed: false
      };
      if (dimensions.width && dimensions.height) {
        const centerX = dimensions.width / 2;
        const centerY = dimensions.height / 2;
        const halfSize = size / 2;
        setPosition({x: centerX - halfSize, y: centerY - halfSize});
      }
      const timer = setTimeout(() => {
        if (isActiveRef.current && dimensions.width && dimensions.height) {
          startAnimation();
        }
      }, 100);
      return () => clearTimeout(timer);
    } else {
      stopAnimation();
    }
  }, [isActive, dimensions.width, dimensions.height, size]);

  // Reiniciar estado de repeticiones cuando cambian pattern o repetitions
  useEffect(() => {
    if (isActive && dimensions.width && dimensions.height) {
      repetitionsCountRef.current = 0;
      cycleStateRef.current = {
        hasBeenRight: false,
        hasBeenLeft: false,
        lastWasCenter: true
      };
      // Resetear el estado del sonido para que continúe funcionando
      soundStateRef.current = {
        lastRightPlayed: false,
        lastLeftPlayed: false
      };
    }
  }, [pattern, repetitions, dimensions.width, dimensions.height]);

  // Mantener la esfera centrada cuando no está activa y se cambia el tamaño
  useEffect(() => {
    if (!isActive && dimensions.width && dimensions.height) {
      const centerX = dimensions.width / 2;
      const centerY = dimensions.height / 2;
      const halfSize = size / 2;
      setPosition({x: centerX - halfSize, y: centerY - halfSize});
    }
  }, [size, dimensions.width, dimensions.height, isActive]);

  if (!dimensions.width || !dimensions.height) {
    return null;
  }

  const overlayMessage = overlayTranslations[language] || overlayTranslations.es;

  return (
    <>
      {/* Fondo negro */}
      <div style={{
        position: isContained ? 'absolute' : 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: isContained ? '100%' : '100vw',
        height: isContained ? '100%' : '100vh',
        backgroundColor: '#000000',
        zIndex: 5,
        pointerEvents: 'none',
      }} />
      {!hideOverlay && !isActive && showOverlay && (
        <div className="emdr-overlay-message">
          {overlayMessage}
        </div>
      )}
      
      {/* Esfera blanca - Siempre visible, se mueve cuando está activa */}
      <div style={{
        position: isContained ? 'absolute' : 'fixed',
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: '50%',
        background: 'radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.9) 0%, rgba(170, 220, 255, 0.6) 45%, rgba(80, 140, 190, 0.35) 100%)',
        border: '1px solid rgba(255, 255, 255, 0.35)',
        boxShadow: '0 12px 30px rgba(0, 0, 0, 0.35), inset 0 2px 8px rgba(255, 255, 255, 0.35)',
        backdropFilter: 'blur(6px) saturate(140%)',
        zIndex: 2100,
        pointerEvents: 'none',
      }} />
    </>
  );
};

export default EMDRVisual;
